import {
  Check, Loader2, Pencil, Trash2,
} from "lucide-react";
import {
  useCallback, useEffect, useRef, useState,
} from "react";
import { cn } from "yeati-ui";
import type { Supplier } from "@/shared/types/supplier.types";
import { Button } from "@/shared/ui/Button";

type SupplierListItemProps = {
  supplier: Supplier
  onSelect: (supplier: Supplier) => void
  onEdit: (supplier: Supplier) => void
  onDelete: (supplier: Supplier) => Promise<void>
};

const CONFIRM_TIMEOUT_MS = 3000;
const TRANSITION_DURATION_MS = 250;

const SupplierListItem = ({
  supplier,
  onSelect,
  onEdit,
  onDelete,
}: SupplierListItemProps) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [showConfirmContent, setShowConfirmContent] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
    if (contentTimerRef.current) {
      clearTimeout(contentTimerRef.current);
      contentTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const exitConfirm = useCallback(() => {
    setIsConfirming(false);
    contentTimerRef.current = setTimeout(() => setShowConfirmContent(false), TRANSITION_DURATION_MS);
  }, []);

  const handleDeleteClick = useCallback(async () => {
    if (!isConfirming) {
      clearTimers();
      setShowConfirmContent(true);
      setIsConfirming(true);
      confirmTimerRef.current = setTimeout(exitConfirm, CONFIRM_TIMEOUT_MS);
      return;
    }

    clearTimers();
    setIsDeleting(true);
    try {
      await onDelete(supplier);
    }
    catch {
      // Error handled by mutation's onError (toast)
    }
    finally {
      setIsDeleting(false);
      setIsConfirming(false);
      setShowConfirmContent(false);
    }
  }, [isConfirming, clearTimers, exitConfirm, onDelete, supplier]);

  const subtitle = [supplier.city, supplier.address]
    .filter(Boolean)
    .join(" — ");

  return (
    <div className="flex items-center justify-between border-b hover:bg-zinc-100/50 transition-colors">
      <button
        type="button"
        onClick={() => onSelect(supplier)}
        className="flex-1 text-left p-3 cursor-pointer"
      >
        <div className="font-medium text-sm">{supplier.name}</div>
        {subtitle && (
          <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
        )}
      </button>
      <div className="flex items-center gap-1 pr-2">
        <Button
          aria-label={`Modifier ${supplier.name}`}
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onEdit(supplier)}
          disabled={isDeleting}
        >
          <Pencil size={14} />
        </Button>
        <Button
          aria-label={isConfirming ? `Confirmer la suppression de ${supplier.name}` : `Supprimer ${supplier.name}`}
          variant={isConfirming ? "destructive" : "ghost"}
          size="sm"
          className={cn(
            "flex flex-row-reverse items-center justify-start pl-2! pr-2! h-8 max-w-8 text-xs text-destructive hover:text-destructive transition-all overflow-hidden whitespace-nowrap",
            "duration-[250ms]",
            isConfirming && "justify-start max-w-100 animate-pulse",
          )}
          onClick={handleDeleteClick}
          disabled={isDeleting}
        >
          {isDeleting
            ? <Loader2 size={14} className="animate-spin" />
            : showConfirmContent
              ? (
                <>
                  <Check size={14} />
                  Êtes-vous sûr ?
                </>
              )
              : <Trash2 size={14} />}
        </Button>
      </div>
    </div>
  );
};

export default SupplierListItem;
